import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Switch,
  Card,
  Row,
  Col,
  Space,
  Button,
  Typography,
  Divider,
  Alert,
  Spin,
  message,
  Table,
  AutoComplete,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import { ContractorUsersTable } from '../../components/admin/ContractorUsersTable';
import { contractorService } from '../../services/contractor.service';
import { profileService } from '../../services/profile.service';
import {
  Contractor,
  CreateContractorData,
  UpdateContractorData,
} from '../../types/contractor.types';
import { State, City } from '../../types/profile.types';

// Phase 4 - US2: Contractor Detail (Create/Edit)

export const ContractorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [searchingCities, setSearchingCities] = useState(false);
  const [formData, setFormData] = useState<Partial<UpdateContractorData>>({
    legalName: '',
    tradeName: '',
    cnpj: '',
    address: '',
    cityId: '',
    active: true,
  });

  useEffect(() => {
    loadStates();
    if (isNew) {
      setContractor(null);
      setFormData({
        legalName: '',
        tradeName: '',
        cnpj: '',
        address: '',
        cityId: '',
        active: true,
      });
      setSelectedStateId('');
      setSelectedCity(null);
      setCityInputValue('');
      setCities([]);
      setLoading(false);
      return;
    }

    loadContractor();
  }, [id, isNew]);

  useEffect(() => {
    if (selectedCity) {
      setCityInputValue(selectedCity.name);
    }
  }, [selectedCity]);



  const loadStates = async () => {
    try {
      const data = await profileService.getStates();
      setStates(data);
    } catch (err) {
      console.error('Failed to load states', err);
    }
  };

  const loadCities = async (stateId: string, searchText: string = '') => {
    try {
      setSearchingCities(true);
      const data = await profileService.getCitiesByState(stateId, searchText);
      setCities(data);
    } catch (err) {
      console.error('Failed to load cities', err);
    } finally {
      setSearchingCities(false);
    }
  };

  const loadContractor = async () => {
    try {
      const data = await contractorService.getContractor(id!);
      setContractor(data);
      setFormData({
        legalName: data.legalName,
        tradeName: data.tradeName,
        cnpj: data.cnpj,
        address: data.address,
        cityId: data.cityId,
        active: data.active,
      });
      if (data.city?.state?.id) {
        setSelectedStateId(data.city.state.id);
        setSelectedCity(data.city as City);
        setCityInputValue(data.city.name);
        // Load cities for the state
        try {
          const citiesList = await profileService.getCitiesByState(data.city.state.id, '');
          setCities(citiesList);
        } catch (error) {
          console.error('Error loading cities:', error);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof CreateContractorData,
    value: string | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStateChange = (stateId: string) => {
    setSelectedStateId(stateId);
    setFormData((prev) => ({ ...prev, cityId: '' }));
    setSelectedCity(null);
    setCities([]);
    setCityInputValue('');
    // Load cities when state is selected
    if (stateId) {
      loadCities(stateId);
    }
  };

  const handleCitySearch = (value: string) => {
    if (selectedStateId) {
      loadCities(selectedStateId, value);
    }
    setCityInputValue(value);
  };

  const handleCitySelect = (_: string, option: any) => {
    const cityId = option?.id as string | undefined;
    if (cityId) {
      setFormData((prev) => ({ ...prev, cityId }));
      const selected = cities.find((c) => c.id === cityId);
      if (selected) {
        setSelectedCity(selected);
        setCityInputValue(selected.name);
      }
    }
  };

  const validateCNPJ = (cnpj: string): boolean => {
    const cleaned = cnpj.replace(/\D/g, '');
    return cleaned.length === 14;
  };

  const buildPayload = (): CreateContractorData => {
    if (!formData.legalName || !formData.cnpj || !formData.address || !formData.cityId) {
      throw new Error('Preencha todos os campos obrigatórios.');
    }

    if (!validateCNPJ(formData.cnpj)) {
      throw new Error('CNPJ deve ter 14 dígitos.');
    }

    return {
      legalName: formData.legalName,
      tradeName: formData.tradeName || '',
      cnpj: formData.cnpj,
      address: formData.address,
      cityId: formData.cityId,
    };
  };

  const handleSaveContractor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = buildPayload();

      if (isNew) {
        const created = await contractorService.createContractor(payload);
        message.success('Contratante criado');
        navigate(`/admin/contractors/${created.id}`);
      } else {
        const updated = await contractorService.updateContractor(id!, {
          ...payload,
          active: formData.active ?? true,
        });
        setContractor(updated);
        setFormData((prev) => ({ ...prev, active: updated.active }));
        message.success('Contratante atualizado');
      }
    } catch (err: any) {
      message.error(err.message || 'Erro ao salvar contratante');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin />;
  if (error) return <Alert type="error" message={error} />;

  if (!isNew && !contractor) return <Notice type="warning" message="Contratante não encontrado" />;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {isNew ? 'Novo Contratante' : contractor?.tradeName || contractor?.legalName}
        </Typography.Title>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/contractors')}>
            Voltar
          </Button>
          <Button type="primary" onClick={() => document.getElementById('contractor-form-submit')?.click()} disabled={saving}>
            {saving ? 'Salvando...' : isNew ? 'Criar contratante' : 'Salvar alterações'}
          </Button>
        </Space>
      </Space>

      <Card title="Dados do Contratante">
        <Form layout="vertical" onSubmitCapture={handleSaveContractor}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Razão Social" required>
                <Input
                  value={formData.legalName || ''}
                  onChange={(e) => handleInputChange('legalName', e.target.value)}
                  placeholder="Digite a razão social"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Nome Fantasia">
                <Input
                  value={formData.tradeName || ''}
                  onChange={(e) => handleInputChange('tradeName', e.target.value)}
                  placeholder="Digite o nome fantasia (opcional)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="CNPJ" required>
                <Input
                  value={formData.cnpj || ''}
                  onChange={(e) => handleInputChange('cnpj', e.target.value)}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Status">
                <Switch
                  checked={formData.active ?? true}
                  onChange={(checked) =>
                    setFormData((prev) => ({ ...prev, active: checked }))
                  }
                  checkedChildren="Ativo"
                  unCheckedChildren="Inativo"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Endereço" required>
                <Input.TextArea
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  placeholder="Endereço completo"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Estado" required>
                <Select
                  value={selectedStateId || undefined}
                  onChange={handleStateChange}
                  placeholder="Selecione o estado"
                  showSearch
                  optionFilterProp="children"
                >
                  {states.map((state) => (
                    <Select.Option key={state.id} value={state.id}>
                      {state.name} ({state.code})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Cidade" required>
                <Input type="hidden" value={formData.cityId || ''} />
                <AutoComplete
                  placeholder={selectedStateId ? 'Digite para buscar cidade' : 'Selecione o estado primeiro'}
                  disabled={!selectedStateId}
                  onSearch={handleCitySearch}
                  onSelect={handleCitySelect}
                  value={cityInputValue}
                  notFoundContent={searchingCities ? 'Buscando...' : 'Nenhuma cidade encontrada'}
                  options={[
                    // Always include selected city first if it exists
                    ...(selectedCity
                      ? [
                          {
                            value: selectedCity.name,
                            label: selectedCity.name,
                            id: selectedCity.id,
                          },
                        ]
                      : []),
                    // Then add other cities not already in the list
                    ...cities
                      .filter((c) => !selectedCity || c.id !== selectedCity.id)
                      .map((city) => ({
                        value: city.name,
                        label: city.name,
                        id: city.id,
                      })),
                  ]}
                  onFocus={() => {
                    // Load cities when dropdown is opened if we have a state selected
                    if (selectedStateId && cities.length === 0) {
                      loadCities(selectedStateId);
                    }
                  }}
                  filterOption={false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              id="contractor-form-submit"
              type="primary"
              htmlType="submit"
              loading={saving}
            >
              {isNew ? 'Criar contratante' : 'Salvar alterações'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {!isNew && contractor && (
        <>
          <Divider />
          <Card title="Usuários do Contratante">
            <ContractorUsersTable contractorId={String(contractor.id)} />
          </Card>
        </>
      )}

      {!isNew && contractor?.contacts && contractor.contacts.length > 0 && (
        <>
          <Divider />
          <Card title={`Contatos (${contractor.contacts.length})`}>
            <Table
              rowKey="id"
              dataSource={contractor.contacts}
              pagination={false}
              columns={[
                { title: 'Nome', dataIndex: 'name', key: 'name' },
                { title: 'Email', dataIndex: 'email', key: 'email' },
                { title: 'Telefone', dataIndex: 'phone', key: 'phone' },
                { title: 'Cargo', dataIndex: 'position', key: 'position' },
                {
                  title: 'Principal',
                  dataIndex: 'isPrimary',
                  key: 'isPrimary',
                  render: (isPrimary: boolean) => (isPrimary ? 'Sim' : 'Não'),
                },
              ]}
            />
          </Card>
        </>
      )}
    </Space>
  );
};
